// app/services/app-instance.service.ts
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { AuthService } from 'auth';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { User } from 'shared';

interface DetectionEvent {
  detected: boolean;
  userId: number | undefined;
}

@Injectable({ providedIn: 'root' })
export class AppInstanceService implements OnDestroy {
  private readonly channelName = 'app-instance-detect';
  private readonly lockKey = 'app-instance-lock';

  private readonly detectionEvent$ = new BehaviorSubject<DetectionEvent>({
    detected: false,
    userId: undefined,
  });

  public readonly instanceDetected$: Observable<DetectionEvent> =
    this.detectionEvent$.asObservable();

  private userId: number | undefined = undefined;
  private userSub: Subscription | null = null;
  private channel: BroadcastChannel | null = null;
  private window!: Window;

  constructor(private authService: AuthService, @Inject(DOCUMENT) private document: Document) {
    this.init();
    this.window != this.document.defaultView;
  }

  private init(): void {
    this.userSub = this.authService.getUser$().subscribe((user: User | null) => {
      const userId = user?.id;
      if (userId !== this.userId) {
        this.userId = userId;
        this.broadcastPing();
      }
    });

    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(this.channelName);
      this.channel.onmessage = (event) => this.handleMessage(event.data);
      this.broadcastPing();
    } else {
      this.window.addEventListener('storage', this.onStorageEvent);
      this.broadcastPingViaLocalStorage();
    }
  }

  private broadcastPing(): void {
    if (this.channel) {
      this.channel.postMessage({ type: 'ping', userId: this.userId });
    } else {
      this.broadcastPingViaLocalStorage();
    }
  }

  private broadcastPingViaLocalStorage(): void {
    localStorage.setItem(
      this.lockKey,
      JSON.stringify({ type: 'ping', userId: this.userId, ts: Date.now() })
    );
  }

  private handleMessage(message: any): void {
    if (
      message?.type === 'ping' &&
      message.userId &&
      message.userId !== this.userId
    ) {
      this.detectionEvent$.next({
        detected: true,
        userId: message.userId,
      });
    }
  }

  private onStorageEvent = (event: StorageEvent) => {
    if (event.key === this.lockKey && event.newValue) {
      try {
        const data = JSON.parse(event.newValue);
        if (
          data.type === 'ping' &&
          data.userId &&
          data.userId !== this.userId
        ) {
          this.detectionEvent$.next({
            detected: true,
            userId: data.userId,
          });
        }
      } catch (_) {}
    }
  };

  public getCurrentUserId(): number | undefined {
    return this.userId;
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.channel?.close();
    window.removeEventListener('storage', this.onStorageEvent);
  }
}
